import * as React from "react"

export const OverlayFocusContext = React.createContext(null)

const useIsomorphicLayoutEffect = typeof window === "undefined" ? React.useEffect : React.useLayoutEffect

function getInvoker(element) {
  if (!element || element.isConnected === false || typeof element.focus !== "function") return null
  return element
}

function getActiveInvoker() {
  if (typeof document === "undefined") return null
  const activeElement = document.activeElement
  if (!activeElement || activeElement === document.body) return null
  return getInvoker(activeElement)
}

export function useOverlayFocus(open, defaultOpen, onOpenChange, modal = true) {
  const invokerRef = React.useRef(null)
  const wasOpenRef = React.useRef(false)
  const captureInvoker = React.useCallback((element) => {
    if (!modal) return
    invokerRef.current = element ? getInvoker(element) : getActiveInvoker()
  }, [modal])
  const handleOpenChange = React.useCallback((nextOpen) => {
    if (modal && nextOpen && (!invokerRef.current || invokerRef.current.isConnected === false)) captureInvoker()
    onOpenChange?.(nextOpen)
  }, [captureInvoker, modal, onOpenChange])
  const isOpen = open ?? defaultOpen ?? false

  useIsomorphicLayoutEffect(() => {
    if (!modal) {
      invokerRef.current = null
      wasOpenRef.current = false
      return
    }
    if (isOpen && !wasOpenRef.current) captureInvoker()
    wasOpenRef.current = isOpen
  }, [captureInvoker, isOpen, modal])

  const focusContext = React.useMemo(() => ({ captureInvoker, invokerRef }), [captureInvoker])
  return { focusContext, handleOpenChange }
}

export function restoreOverlayFocus(event, focusContext) {
  if (event.defaultPrevented) return
  const invokerRef = focusContext?.invokerRef
  const invoker = invokerRef?.current
  if (!invoker || invoker.isConnected === false || typeof invoker.focus !== "function") {
    if (invoker?.isConnected === false) invokerRef.current = null
    return
  }
  invoker.focus({ preventScroll: true })
  event.preventDefault()
  invokerRef.current = null
}
